/**
 * Wire contract for Keycloak's headless browser-flow extension.
 *
 * Source of truth: keycloak-2fa-sms-authenticator `docs/headless-login-ui-guide.md`.
 * Keep the shapes here aligned with that document rather than with whatever a
 * particular realm happens to return today.
 */

export const STEP_TYPES = [
  'identify',
  'username_password',
  'password',
  'create_pin',
  'pin',
  'reset_pin',
  'phone_number',
  'sms_otp',
  'email_otp',
  'totp',
  'totp_setup',
  'webauthn',
  'webauthn_register',
  'passkey',
  'passkey_register',
  'passkey_conditional',
  'identity_provider',
  'broker_review_profile',
  'broker_verify_email',
  'broker_confirm_link',
  'broker_confirm_override',
  'broker_reauthenticate',
  'consent',
  'registration',
  'organization',
  'recovery_code',
  'recovery_codes_setup',
  'security_questions',
  'update_profile',
  'verify_profile',
  'verify_email',
  'update_email',
  'update_email_verification',
  'update_locale',
  'delete_credential',
  'delete_account',
  'account_deleted',
  'reset_credentials_identify',
  'reset_email_sent',
  'reset_password',
  'verify_identity',
  'accept_terms',
  'update_password',
] as const;

export type HeadlessAuthStepType = (typeof STEP_TYPES)[number];

export function isKnownStepType(type: string): type is HeadlessAuthStepType {
  return (STEP_TYPES as readonly string[]).includes(type);
}

export interface HeadlessAuthField {
  name: string;
  type: string;
  required?: boolean;
  autocomplete?: string | null;
  minLength?: number | null;
  maxLength?: number | null;
  readOnly?: boolean;
  value?: string | null;
  values?: string[];
  options?: Array<{ label?: string; value: string }>;
}

/** An alternative operation (`forgot_password`, `resend_code`, ...) the step advertises. */
export interface HeadlessAuthAlternativeAction {
  name: string;
  available: boolean;
}

export interface HeadlessAuthStepError {
  code: string;
  message: string;
  field?: string | null;
}

export interface HeadlessAuthProtocolContract {
  version: string;
  capabilities: string[];
}

/**
 * The step exactly as Keycloak sends it. `action` is the opaque continuation URL,
 * which the guide classes as a credential — it never leaves
 * `HeadlessAuthFlowService`, so callers see {@link HeadlessAuthStep} instead.
 */
export interface HeadlessAuthWireStep {
  status: 'interaction_required' | 'complete';
  type: string;
  action: string | null;
  expiresIn: number;
  fields: HeadlessAuthField[];
  actions: HeadlessAuthAlternativeAction[];
  metadata: Record<string, unknown>;
  error: HeadlessAuthStepError | null;
  protocol?: HeadlessAuthProtocolContract;
}

/** The step as callers see it: everything except the continuation URL. */
export type HeadlessAuthStep = Omit<HeadlessAuthWireStep, 'action'>;

/** OIDC token endpoint response, before it is mapped to {@link TokenSet}. */
export interface HeadlessAuthTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type: string;
  expires_in: number;
  scope?: string;
}

export interface TokenSet {
  accessToken: string;
  refreshToken?: string;
  idToken: string;
  expiresIn: number;
  scope?: string;
}

/**
 * Every service call resolves with either the next step to render or the tokens
 * the completed flow issued. Callers decide what to do with the tokens; the
 * service never stores them.
 */
export type HeadlessAuthFlowResult =
  | { kind: 'step'; step: HeadlessAuthStep }
  | { kind: 'tokens'; tokens: TokenSet };

/** The per-login-attempt PKCE/state/nonce record held in sessionStorage. */
export interface HeadlessAuthTransaction {
  state: string;
  nonce: string;
  codeVerifier: string;
  createdAt: number;
  clientId: string;
  redirectUri: string;
}
